import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies, MOVIE_DISCLAIMER } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import RatingBadge from "@/components/base/RatingBadge";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageContainer from "@/components/base/PageContainer";
import SectionHeading from "@/components/base/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id)
      .map((m) => {
        let score = 0;
        if (m.type === movie.type) score += 2;
        if (m.region === movie.region) score += 1;
        score += m.genres.filter((g) => movie.genres.includes(g)).length * 3;
        score += m.tags.filter((t) => movie.tags.includes(t)).length * 2;
        return { movie: m, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((item) => item.movie);
  }, [movie]);

  usePageMeta(
    movie ? `${movie.title} - 影视影院影视详情介绍` : "影视详情 - 影视影院",
    movie
      ? `${movie.title}（${movie.year}）影视简介：${movie.summary.slice(0, 70)}`
      : "影视影院影视详情页",
    movie ? `影视影院,${movie.title},${movie.genres.join(",")},影视详情` : "影视影院,影视详情"
  );

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = async () => {
    if (!movie) return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制到剪贴板（演示效果）");
    } catch {
      showToast("分享链接：" + window.location.href);
    }
  };

  if (!movie) {
    return (
      <PageContainer className="py-20">
        <div className="flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
            <i className="ri-movie-line text-3xl" />
          </span>
          <h1 className="font-heading font-extrabold text-xl text-foreground-900">未找到该影视作品</h1>
          <p className="mt-2 text-sm text-foreground-500">该作品可能已下架或链接有误，去分类页看看吧。</p>
          <Link
            to="/category"
            className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-layout-grid-line" />
            浏览影视分类
          </Link>
        </div>
      </PageContainer>
    );
  }

  const favorite = isFavorite(movie.id);

  return (
    <div className="w-full bg-background-100">
      <PageContainer className="py-6 md:py-8">
        <Breadcrumb
          items={[
            { label: movie.type === "movie" ? "电影" : "电视剧", to: `/category?type=${movie.type}` },
            { label: movie.title },
          ]}
        />

        <div className="mt-4 rounded-lg bg-background-50 border border-background-200 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] lg:grid-cols-[320px_1fr]">
            <div className="relative w-full aspect-[2/3] md:aspect-auto md:h-full bg-background-200">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - 影视影院影视详情介绍`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="p-5 md:p-7 flex flex-col">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-md bg-primary-600 text-white text-xs font-label">
                  {movie.type === "movie" ? "电影" : "电视剧"}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs font-label">
                  {movie.region}
                </span>
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category?genre=${encodeURIComponent(g)}`}
                    className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-label hover:bg-accent-200 transition-colors cursor-pointer"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
                {movie.title}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                <RatingBadge rating={movie.rating} size="md" />
                <span>上映日期：{movie.releaseDate}</span>
                <span>时长：{movie.duration}</span>
                <span>年份：{movie.year}</span>
              </div>

              <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div className="flex gap-2">
                  <dt className="text-foreground-400 shrink-0">导演</dt>
                  <dd className="text-foreground-800">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-400 shrink-0">类型</dt>
                  <dd className="text-foreground-800">{movie.genres.join(" / ")}</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="text-foreground-400 shrink-0">主演</dt>
                  <dd className="text-foreground-800">{movie.actors.join("、")}</dd>
                </div>
              </dl>

              <div className="mt-auto pt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                    favorite
                      ? "bg-accent-500 text-white"
                      : "bg-background-100 border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
                  }`}
                >
                  <i className={favorite ? "ri-heart-3-fill text-lg" : "ri-heart-3-line text-lg"} />
                  {favorite ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-share-forward-line text-lg" />
                  分享
                </button>
                <Link
                  to={`/category?type=${movie.type}`}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-sm font-label text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-price-tag-3-line" />
                  所属分类：{movie.type === "movie" ? "电影" : "电视剧"}
                </Link>
              </div>
              <p className="mt-3 text-xs text-foreground-400">
                收藏与分享均为纯浏览器本地演示效果，不涉及账号存储与真实分享服务。
              </p>
            </div>
          </div>
        </div>

        {/* 简介 */}
        <section className="mt-6 rounded-lg bg-background-50 border border-background-200 p-5 md:p-7">
          <SectionHeading title="剧情简介" icon="ri-book-open-line" />
          <p className="text-sm md:text-base text-foreground-700 leading-loose">{movie.summary}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {movie.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </section>

        {/* 相关推荐 */}
        {related.length > 0 && (
          <section className="mt-8">
            <SectionHeading title="相关推荐" subtitle="根据类型、地区与标签为你推荐" icon="ri-thumb-up-line" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        )}

        <p className="mt-8 text-xs text-foreground-400 text-center leading-relaxed">{MOVIE_DISCLAIMER}</p>
      </PageContainer>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-md bg-foreground-950 text-white text-sm shadow-lg animate-fade-up">
          {toast}
        </div>
      )}
    </div>
  );
}